import type { ApprovalResponse } from '@common/types';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { useCallback, useState } from 'react';
import {
    getIntakeOnlyFormStatus,
    getPostIntakeFormStatus,
} from '@/services/ClientSideTemplateFormService';
import { getIntakeFormStatus } from '@/services/EmailApprovalService';
import {
    extractApiError,
    type FormEntryMode,
    getApiErrorLanguage,
    getApiErrorMessage,
    getApiErrorStatus,
} from '../types';

export type StatusValidationResult = { valid: true } | { valid: false; status: ApprovalResponse };

interface UseEntryModeStatusReturn {
    /**
     * The current intake form status (set when validation fails or status is fetched).
     * Used to display error/expiry pages.
     */
    intakeFormStatus: ApprovalResponse | undefined;

    /**
     * Set the intake form status manually (e.g., from API response data).
     */
    setIntakeFormStatus: (status: ApprovalResponse | undefined) => void;

    /**
     * Language of the intake template as reported by the status endpoints.
     * Available before the full template (and its metadata) has been fetched,
     * so pre-verification UI can render in the correct language.
     */
    statusLanguage: TemplateFormLanguage | undefined;

    /**
     * Record language from a status response (or error payload) so entry paths that
     * call status APIs directly can localize pre-verification UI, matching V1.
     */
    setStatusLanguage: (language: TemplateFormLanguage | undefined) => void;

    /**
     * Validates the form status based on entry mode before performing an action.
     * Returns { valid: true } if the form is still active, or { valid: false, status }
     * if the form has expired, been submitted, etc.
     *
     * @param options.blockPostIntakeOnForbiddenOnly - If true (default: false), for postIntake mode,
     *        only block if the status is 'Forbidden'. Used for final submission where we want to
     *        allow resubmission in some cases.
     */
    validateStatus: (options?: {
        blockPostIntakeOnForbiddenOnly?: boolean;
    }) => Promise<StatusValidationResult>;

    /**
     * Validates status and updates internal state if invalid.
     * Returns true if valid, false if invalid (and intakeFormStatus has been set).
     * Convenience wrapper around validateStatus() for simpler call sites.
     */
    validateStatusAndUpdate: (options?: {
        blockPostIntakeOnForbiddenOnly?: boolean;
    }) => Promise<boolean>;
}

/**
 * Hook to manage form status validation based on entry mode.
 *
 * Centralizes the status checking logic that determines if an intake form
 * is still active (vs expired, already submitted, etc.) before allowing
 * draft saves or final submissions.
 */
export function useEntryModeStatus(entryMode: FormEntryMode): UseEntryModeStatusReturn {
    const [intakeFormStatus, setIntakeFormStatus] = useState<ApprovalResponse | undefined>();
    const [statusLanguage, setStatusLanguage] = useState<TemplateFormLanguage | undefined>();

    const validateStatus = useCallback(
        async (options?: {
            blockPostIntakeOnForbiddenOnly?: boolean;
        }): Promise<StatusValidationResult> => {
            const { blockPostIntakeOnForbiddenOnly = false } = options ?? {};

            try {
                let statusResponse: ApprovalResponse | undefined;
                switch (entryMode.type) {
                    case 'manualProspect':
                        statusResponse = (
                            await getIntakeFormStatus(entryMode.instanceId, entryMode.prospectId)
                        ).data;
                        break;
                    case 'draft':
                        statusResponse = (
                            await getIntakeOnlyFormStatus(entryMode.draftFormSubmissionId)
                        ).data;
                        break;
                    case 'postIntake':
                        statusResponse = (await getPostIntakeFormStatus(entryMode.formSubmissionId))
                            .data;
                        break;
                    case 'fresh':
                        // No status check needed for fresh entries
                        break;
                }
                if (statusResponse?.Language) {
                    setStatusLanguage(statusResponse.Language);
                }
                return { valid: true };
            } catch (error: unknown) {
                const apiError = extractApiError(error);
                const errorStatus = getApiErrorStatus(apiError) || 'Failed';
                const errorLanguage = getApiErrorLanguage(apiError);
                if (errorLanguage) {
                    setStatusLanguage(errorLanguage);
                }

                // For postIntake, optionally only block on Forbidden status
                if (
                    entryMode.type === 'postIntake' &&
                    blockPostIntakeOnForbiddenOnly &&
                    errorStatus !== 'Forbidden'
                ) {
                    return { valid: true };
                }

                return {
                    valid: false,
                    status: {
                        Status: errorStatus,
                        Message: getApiErrorMessage(apiError),
                    },
                };
            }
        },
        [entryMode],
    );

    const validateStatusAndUpdate = useCallback(
        async (options?: { blockPostIntakeOnForbiddenOnly?: boolean }): Promise<boolean> => {
            const result = await validateStatus(options);
            if (!result.valid) {
                setIntakeFormStatus(result.status);
            }
            return result.valid;
        },
        [validateStatus],
    );

    return {
        intakeFormStatus,
        setIntakeFormStatus,
        statusLanguage,
        setStatusLanguage,
        validateStatus,
        validateStatusAndUpdate,
    };
}
